import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { getMovieById, movies } from '@/mocks/movies';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  if (!movie) {
    return <NotFound />;
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.image,
            dateCreated: `${movie.year}`,
            director: { '@type': 'Person', name: movie.director },
            actor: movie.cast.map((name) => ({ '@type': 'Person', name })),
            url: `https://www.dashixiong.tv/article/${movie.id}`,
          }),
        }}
      />

      <Navbar />

      <main className="pt-20 pb-12">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: movie.category, href: `/category/${movie.categorySlug}` },
              { label: movie.title },
            ]}
          />

          <article className="mt-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Poster */}
              <div className="flex-shrink-0 w-full md:w-[300px]">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={`${movie.title} - ${movie.category}高清在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  <span className="bg-primary-500/20 text-primary-400 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                    {movie.category}
                  </span>
                  {movie.isHot && (
                    <span className="bg-accent-500/20 text-accent-400 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">热播中</span>
                  )}
                  {movie.isNew && (
                    <span className="bg-primary-500/20 text-primary-400 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">最新上线</span>
                  )}
                  <span className="flex items-center gap-1 text-sm text-primary-400">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                    {movie.rating}
                  </span>
                </div>

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-background-100 rounded-lg p-3">
                    <p className="text-xs text-foreground-500">年份</p>
                    <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.year}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3">
                    <p className="text-xs text-foreground-500">地区</p>
                    <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.region}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3">
                    <p className="text-xs text-foreground-500">时长/集数</p>
                    <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.duration}</p>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-sm text-foreground-500">导演</p>
                  <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.director}</p>
                </div>

                <div className="mt-4">
                  <p className="text-sm text-foreground-500">主演</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {movie.cast.map((actor) => (
                      <span
                        key={actor}
                        className="px-3 py-1 bg-background-100 border border-background-200 rounded-full text-xs text-foreground-300 whitespace-nowrap"
                      >
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <h2 className="text-base font-semibold text-foreground-200 font-heading">剧情简介</h2>
                  <p className="text-sm text-foreground-400 mt-2 leading-relaxed">{movie.description}</p>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-fill text-sm"></i>
                    </div>
                    立即播放
                  </button>
                  <button className="px-4 py-2.5 bg-background-100 hover:bg-background-200 border border-background-200 text-foreground-300 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line text-sm"></i>
                    </div>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            {/* Related Movies */}
            {relatedMovies.length > 0 && (
              <section className="mt-14">
                <h2 className="text-xl font-bold text-foreground-100 font-heading mb-6">
                  相关{movie.category}推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}

            {/* Back link */}
            <div className="mt-10">
              <Link
                to={`/category/${movie.categorySlug}`}
                className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </div>
                返回{movie.category}列表
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}